import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCMS } from '@/hooks/useCMS';

const DEFAULT = {
  label: 'Phoenix Georgia — 1999',
  headline_line1: 'პრემიუმ',
  headline_line2: 'მასალები',
  headline_line3: 'თქვენი',
  headline_line4: 'სივრცისთვის',
  subtext: 'საქართველოს ყველაზე სანდო მომწოდებელი — Laminam, Ardex, Tecnopol, Fodbo და სხვა მსოფლიო ბრენდები.',
  btn_catalog_label: 'კატალოგი',
  btn_catalog_href: '/products',
  btn_call_label: 'დარეკეთ',
  btn_call_href: 'tel:+995599872020',
  image: 'https://readdy.ai/api/search-image?query=luxurious%20modern%20interior%20design%20showroom%20with%20premium%20stone%20flooring&width=1600&height=900&seq=hero1&orientation=landscape',
  image_alt: 'Phoenix Georgia პრემიუმ სამშენებლო მასალები',
  badge_label: 'ოფიციალური დისტრიბუტორი',
  badge_brands: 'Laminam · Ardex · Tecnopol · Fodbo',
  scroll_label: 'scroll',
  stats: [
    { number: '25+', label: 'წელი' },
    { number: '5K+', label: 'პროექტი' },
    { number: '50+', label: 'ბრენდი' },
  ],
};

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const d = useCMS('hero', DEFAULT);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex overflow-hidden bg-[#F5F2EE]">
      <div className="relative z-10 flex flex-col justify-between w-full lg:w-[52%] px-8 md:px-14 pt-36 pb-12">
        <div className="flex items-center gap-3" style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(16px)', transition: 'all 0.7s ease 0.1s' }}>
          <div className="w-6 h-px bg-[#ec3145]"></div>
          <span className="text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145]">{d.label}</span>
        </div>
        <div className="flex-1 flex flex-col justify-center py-12">
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(30px)', transition: 'all 0.8s ease 0.25s' }}>
            <h1 className="font-serif text-[clamp(3.5rem,7vw,7rem)] font-bold text-[#1C1C1C] leading-[0.95] tracking-tight mb-8">
              {d.headline_line1}<br />
              <span className="text-[#ec3145]">{d.headline_line2}</span><br />
              {d.headline_line3}<br />
              {d.headline_line4}
            </h1>
          </div>
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'none' : 'translateY(20px)', transition: 'all 0.8s ease 0.45s' }}>
            <p className="text-[#1C1C1C]/50 text-base md:text-lg leading-relaxed max-w-md mb-10 font-light">{d.subtext}</p>
            <div className="flex flex-wrap gap-4">
              <Link to={d.btn_catalog_href} className="inline-flex items-center gap-3 bg-[#1C1C1C] text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 hover:bg-[#ec3145] transition-colors duration-400 cursor-pointer whitespace-nowrap">
                {d.btn_catalog_label}
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
              <a href={d.btn_call_href} className="inline-flex items-center gap-3 border border-[#1C1C1C]/20 text-[#1C1C1C] text-xs font-bold tracking-[0.2em] uppercase px-8 py-4 hover:border-[#ec3145] hover:text-[#ec3145] transition-colors duration-300 cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-sm"></i>
                {d.btn_call_label}
              </a>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6 border-t border-[#1C1C1C]/10 pt-8" style={{ opacity: loaded ? 1 : 0, transition: 'all 0.8s ease 0.65s' }}>
          {(d.stats || []).map((s: any, i: number) => (
            <div key={i}>
              <div className="font-serif text-3xl md:text-4xl font-bold text-[#1C1C1C]">{s.number}</div>
              <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#1C1C1C]/40 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden lg:block absolute right-0 top-0 bottom-0 w-[52%]" style={{ opacity: loaded ? 1 : 0, transition: 'opacity 1s ease 0.3s' }}>
        <img src={d.image} alt={d.image_alt} className="w-full h-full object-cover object-center" />
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#ec3145]"></div>
        <div className="absolute bottom-12 left-8 bg-white px-6 py-5">
          <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#ec3145] mb-1">{d.badge_label}</div>
          <div className="text-sm font-semibold text-[#1C1C1C]">{d.badge_brands}</div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 lg:left-14 lg:translate-x-0 flex flex-col items-center gap-2 z-10" style={{ opacity: loaded ? 1 : 0, transition: 'opacity 1s ease 1s' }}>
        <div className="w-px h-10 bg-[#1C1C1C]/20 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full bg-[#ec3145] animate-[scrollLine_2s_ease-in-out_infinite]" style={{ height: '40%' }}></div>
        </div>
        <span className="text-[9px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 rotate-90 origin-center mt-2">{d.scroll_label}</span>
      </div>
    </section>
  );
}
